import {IHedgeMatrixDataService} from "./hedge-matrix-data-service.interface";
import {LastQuoteCacheService} from "../../last-quote-cache.service";
import {ApgDataService} from "../../adjustment-pricing-grid/services/apg-data.service";
import {HedgePositionsService} from "../positions-section/hedge-positions/hedge-positions.service";
import {HedgeMatrixCellData} from "./hedge-matrix-cell.data";
import {HedgeData} from "./hedge-data";
import {
    BucketRoleColor,
    GetOptionChainShellResponse,
    OptionExpirationDescriptor
} from "../../shell-communication/shell-dto-protocol";
import {
    convertFriendlyExpirationToIso,
    determineDateFormat,
    findHCF,
    getShortUUID,
    isFuturesTicker,
    isValidNumber,
    isVoid,
    jsonCloneObject
} from "../../utils";
import {HedgeMatrixStateSnapshot, MatrixTransactionDelta} from "./hedge-matrix-undo.service";
import * as Enumerable from "linq";
import {makeOptionTicker, parseOptionTicker} from "../../options-common/options.model";
import {HedgeMatrixTransLeg} from "./hedge-matrix-trans.leg";
import {HedgePosition} from "../data-model/hedge-position";
import {ApgPortfolio} from "../../adjustment-pricing-grid/model/ApgPortfolio";
import {BeforePositionDto} from "../../adjustment-pricing-grid/model/BeforePositionDto";
import {OptionsChainService} from "../../option-chains.service";
import {DateTime} from "luxon-business-days";
import {EsFairValueService} from "../services/es-fair-value.service";
import {HgUnderlyingContext} from "../data-model/hg-underlying-context";
import {TradingInstrumentsService} from "../../trading-instruments/trading-instruments-service.interface";

export class HedgeMatrixDataService implements IHedgeMatrixDataService {

    constructor(
        public readonly portfolio: ApgPortfolio,
        private readonly _optionChainService: OptionsChainService,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _apgDataService: ApgDataService,
        private readonly _hedgePositionsService: HedgePositionsService,
        private readonly _esFairValueService: EsFairValueService,
        private readonly _tiService: TradingInstrumentsService
    ) {
    }


    private _cellDataIndex: Record<number, Record<string, Record<string, HedgeMatrixCellData>>> = {};
    private _existingHedges: HedgeData[] = [];
    private _newHedges: HedgeData[] = [];
    private _portfolioPositionsByStrike: Record<number, string[]> = {};
    private _underlying: string;
    private _portfolioDefaultQty: number;
    private _chain: GetOptionChainShellResponse;
    private _strikes: number[];

    /** Per-underlying contexts for non-base underlyings (e.g., ES contracts) */
    private _underlyingContexts: Map<string, HgUnderlyingContext> = new Map();

    //  expiration: { strike: { hedge: [pnl] } }
    private _callExpirationPnl: Record<string, Record<number, Record<string, number>>> = {};
    private _putExpirationPnl: Record<string, Record<number, Record<string, number>>> = {}

    reset() {
        this._cellDataIndex = {};
        this._existingHedges = [];
        this._newHedges = [];
        this._portfolioPositionsByStrike = {};
        this._underlying = undefined;
        this._portfolioDefaultQty = undefined;
        this._chain = undefined;
        this._strikes = [];
        this._underlyingContexts = new Map();
        this._callExpirationPnl = {};
        this._putExpirationPnl = {};
    }

    async onPortfolioSelected(): Promise<void> {

        this.reset();

        this._underlying = await this._apgDataService.getUnderlyingOfPortfolio(this.portfolio);

        this._portfolioDefaultQty = await this._apgDataService.getDefaultQtyForPortfolio(this.portfolio);

        this._chain = await this._optionChainService.getChain(this._underlying)

        const hedgePositions = await this._hedgePositionsService
            .getHedgePositions(this.portfolio);

        // Detect non-base underlyings and load their contexts
        const otherUnderlyings = Enumerable.from(hedgePositions)
            .where(x => !isVoid(x.asset) && x.asset !== this._underlying)
            .select(x => x.asset)
            .distinct()
            .toArray();

        for (const otherUl of otherUnderlyings) {
            await this.ensureUnderlyingContext(otherUl);
        }

        const hedges = Enumerable.from(hedgePositions)
            .groupBy(x => x.groupId)
            .select(x => {
                const firstLeg = x.first();
                const underlying = firstLeg.asset || this._underlying;

                const hedge: HedgeData = {
                    name: firstLeg.label,
                    id: x.key(),
                    color: firstLeg.color,
                    legs: x.toArray(),
                    type: firstLeg.type,
                    underlying,
                };
                return hedge;
            })
            .toArray();

        const sorted = hedges
            .sort((grpA, grpB) => {

                const grpAOrder = grpA.legs[0].groupOrder;
                const grpBOrder = grpB.legs[0].groupOrder;

                if (isValidNumber(grpAOrder) && isValidNumber(grpBOrder)) {
                    return grpAOrder - grpBOrder;
                } else {
                    const lastStrikeA = grpA.legs[grpA.legs.length - 1].strike;
                    const lastStrikeB = grpB.legs[grpB.legs.length - 1].strike;
                    return lastStrikeB - lastStrikeA;
                }
            });

        this._existingHedges = sorted;

        hedgePositions.forEach(y => {
                const posUnderlying = y.asset || this._underlying;
                const isNonBase = posUnderlying !== this._underlying;
                const fairValue = isNonBase
                    ? (this._underlyingContexts.get(posUnderlying)?.fairValueOffset || 0)
                    : 0;

                // SPX-equivalent strike for row indexing
                const indexStrike = y.strike - fairValue;

                let hedgeContainer = this._cellDataIndex[indexStrike];

                if (isVoid(hedgeContainer)) {
                    hedgeContainer = {};
                    this._cellDataIndex[indexStrike] = hedgeContainer;
                }

                let expirationContainer = hedgeContainer[y.groupId];

                if (isVoid(expirationContainer)) {
                    expirationContainer = {};
                    hedgeContainer[y.groupId] = expirationContainer;
                }

                const cData: HedgeMatrixCellData = {
                    ticker: y.ticker,
                    underlying: posUnderlying,
                    hedgeId: y.groupId,
                    color: y.color,
                    label: y.label,
                    optionType: y.type,
                    strike: indexStrike,
                    actualStrike: y.strike,
                    expiration: y.expiration,
                    qty: y.qty
                };

                expirationContainer[y.expiration] = cData;
            });

        const portfolioPositions = await this.getPortfolioPositions(this.portfolio);

        portfolioPositions
            .flatMap(x => x)
            .forEach(x => {

                const ticker = parseOptionTicker(x.ticker);

                const role = x.role;
                const strike = ticker!.strike;
                const color = BucketRoleColor[role];
                const type = ticker.type;

                let container = this._portfolioPositionsByStrike[strike];

                if (isVoid(container)) {
                    container = [];
                    this._portfolioPositionsByStrike[strike] = container;
                }

                if (container.indexOf(color) < 0) {
                    container.push(color);
                }
            });
    }

    getPortfolioUnderlying(): string {
        return this._underlying;
    }

    /** Returns all non-base underlying contexts (ES contracts with positions) */
    getUnderlyingContexts(): Map<string, HgUnderlyingContext> {
        return this._underlyingContexts;
    }

    /** Returns tickers of non-base underlyings that have hedge positions */
    getActiveNonBaseUnderlyings(): string[] {
        return Array.from(this._underlyingContexts.keys());
    }

    /** Returns the fair value offset for a non-base underlying */
    getFairValueOffset(underlying: string): number {
        return this._underlyingContexts.get(underlying)?.fairValueOffset || 0;
    }

    /** Converts an SPX-equivalent strike to the actual strike for a given underlying */
    getActualStrike(spxEquivalentStrike: number, underlying: string): number {
        if (underlying === this._underlying) {
            return spxEquivalentStrike;
        }
        return spxEquivalentStrike + this.getFairValueOffset(underlying);
    }

    /**
     * Ensures the underlying context (chain, fair value, trading instrument) is loaded.
     *
     * Returns true when the context is ready and consistent, false when it could not be loaded.
     *
     * CR-20260924-002: `knownFairValue` is the gap the backend ES lens used for an ORION answer.
     * A context created here adopts it instead of a second fair-value read (two reads a moment
     * apart can land five points apart, and then the card's strike and the silo's row disagree);
     * a context that already exists on a DIFFERENT number reports false, so the caller refuses
     * rather than misplace a strike. Absent = the historic behaviour: read the fair value.
     */
    async ensureUnderlyingContext(underlying: string, date?: Date, knownFairValue?: number): Promise<boolean> {
        if (underlying === this._underlying) {
            return true;
        }
        const hasKnownFairValue = typeof knownFairValue === 'number' && isFinite(knownFairValue);
        const existing = this._underlyingContexts.get(underlying);
        if (existing) {
            if (hasKnownFairValue && existing.fairValueOffset !== knownFairValue) {
                console.warn(`ES silo ${underlying} is built on fair value ${existing.fairValueOffset}; the answer used ${knownFairValue}`);
                return false;
            }
            return true;
        }
        try {
            const chain = await this._optionChainService.getChain(underlying);
            const fairValue = hasKnownFairValue
                ? knownFairValue
                : await this._esFairValueService.getFairValue(underlying, date);
            const ti = this._tiService.getInstrumentByTicker(underlying);
            this._underlyingContexts.set(underlying, {
                underlying,
                chain,
                fairValueOffset: fairValue,
                tradingInstrument: ti
            });
            return true;
        } catch (e) {
            console.error(`Failed to load context for underlying: ${underlying}`, e);
            return false;
        }
    }

    /** Returns the option chain for a given underlying */
    getChainForUnderlying(underlying: string): GetOptionChainShellResponse {
        if (underlying === this._underlying) {
            return this._chain;
        }
        return this._underlyingContexts.get(underlying)?.chain;
    }

    // Replace metadata only. Held legs, transaction deltas, Preview ownership,
    // undo history and per-silo fair-value offsets remain the same objects.
    refreshOptionSeries(ticker?: string): void {
        if (!ticker || ticker === this._underlying) {
            if (this._optionChainService.isChainConfirmed(this._underlying)) {
                this._chain = this._optionChainService.getCachedChain(this._underlying);
            }
        }
        this._underlyingContexts.forEach((context, underlying) => {
            if ((!ticker || ticker === underlying) && this._optionChainService.isChainConfirmed(underlying)) {
                context.chain = this._optionChainService.getCachedChain(underlying);
            }
        });
    }

    /** Returns hedges filtered by underlying */
    getHedgesByUnderlying(underlying: string): HedgeData[] {
        return this.getHedges().filter(h => (h.underlying || this._underlying) === underlying);
    }

    /** Checks if a hedge belongs to a non-base underlying */
    isNonBaseHedge(hedgeId: string): boolean {
        const hedge = this.getHedge(hedgeId);
        return hedge && hedge.underlying !== this._underlying;
    }

    getCellData(strike: number, hedgeId: string): HedgeMatrixCellData[] {
        let hedgeContainer = this._cellDataIndex[strike];

        if (isVoid(hedgeContainer)) {
            return null;
        }

        const expirationContainer = hedgeContainer[hedgeId];

        if (isVoid(expirationContainer)) {
            return null;
        }

        return Object.values(expirationContainer);
    }

    removeHedgeCellData(cellData: HedgeMatrixCellData) {
        const strike = cellData.strike;
        const hedgeId = cellData.hedgeId;
        const expiration = cellData.expiration;

        try {
            delete this._cellDataIndex[strike][hedgeId][expiration];
        } catch {
        }
    }

    getLegStrikes(): number[] {
        const strikes = Object.keys(this._cellDataIndex)
            .map(x => parseInt(x))
            .filter(x => isValidNumber(x, true));
        return strikes;
    }

    getTotalQtyForStrike(strike: number, side: 'Call' | 'Put', visible: string[]): number {

        const hasMixed = visible.some(x => this.doesHaveMultipleExpirations(x));

        if (hasMixed) {
            return null;
        }

        const strikeContainer = this._cellDataIndex[strike];

        if (isVoid(strikeContainer)) {
            return null;
        }

        const hedgeContainers = Object.values(strikeContainer);

        if (isVoid(hedgeContainers)) {
            return null;
        }

        const cellDatas = hedgeContainers
            .flatMap(x => Object.values(x) as HedgeMatrixCellData[]);

        const total = Enumerable.from(cellDatas)
            .where(x => x.optionType === side)
            .where(x => visible.indexOf(x.hedgeId) !== -1)
            .select(x => {
                const qty = x.qty || 0;
                const transQty = x.transQty || 0;
                const totalQty = qty + transQty;
                return totalQty;
            })
            .aggregate(0, (p, c) => p + c);

        return total;
    }

    getHedges(): HedgeData[] {
        return this._newHedges.concat(this._existingHedges).sort(
            (a, b) => a.type.localeCompare(b.type)
        );
    }

    getHedge(id: string) {
        const hedgeData = this.getHedges().find(x => x.id === id);
        return hedgeData;
    }

    getPortfolioLegColorByStrike(strike: number): string[] {
        const arr = this._portfolioPositionsByStrike[strike] || [];
        return arr.slice();
    }

    getPortfolioPositionStrikes(): number[] {
        return Object.keys(this._portfolioPositionsByStrike).map(x => parseInt(x));
    }

    getTickerForCell(strike: number, expiration: string, side: "Call" | "Put", underlying?: string): string {

        const expirationDateFormat = determineDateFormat(expiration);

        if (expirationDateFormat === 'friendly') {
            expiration = convertFriendlyExpirationToIso(expiration);
        }

        const chain = (!isVoid(underlying) && underlying !== this._underlying)
            ? this.getChainForUnderlying(underlying)
            : this._chain;

        if (isVoid(chain)) {
            return null;
        }

        const expDescriptor = chain.expirations
            .find(x => x.optionExpirationDate
                .indexOf(expiration) >= 0);

        const ticker = makeOptionTicker(expDescriptor, side, strike);

        return ticker;

    }

    getNearestExpiration(underlying?: string): OptionExpirationDescriptor {
        const chain = (!isVoid(underlying) && underlying !== this._underlying)
            ? this.getChainForUnderlying(underlying)
            : this._chain;
        return chain?.expirations?.filter(x => x.visible)[0];
    }

    getAvailableExpirationDates(underlying?: string): string[] {
        const chain = (!isVoid(underlying) && underlying !== this._underlying)
            ? this.getChainForUnderlying(underlying)
            : this._chain;
        return (chain?.expirations ?? [])
            .filter(x => x.daysToExpiration >= 0)
            .map(x => x.optionExpirationDate);
    }

    // The full descriptors, VISIBLE-filtered (CR-20260804-001) — the embedded Select Copilot's
    // expiration universe. Visible, not dte>=0: the copilot contract is the OPG panel's own
    // ("non-visible expirations must never leak"), and visible is what getNearestExpiration
    // already keys on.
    getAvailableExpirations(underlying?: string): OptionExpirationDescriptor[] {
        if (!this._optionChainService.isChainConfirmed(underlying || this._underlying)) { return []; }
        const chain = (!isVoid(underlying) && underlying !== this._underlying)
            ? this.getChainForUnderlying(underlying)
            : this._chain;
        return (chain?.expirations ?? []).filter(x => x.visible);
    }

    // CR-20260901-005 — undo/redo state capture. The cell index is pure data (primitives only),
    // so a JSON clone both ways keeps stack entries isolated from live mutations. Hedge-level
    // mutable fields ride along by REFERENCE (they are replaced wholesale, never edited in place);
    // hedge identity (the HedgeData instances) is deliberately NOT part of the snapshot — column
    // defs and CoPilot maps close over those instances, and hedge add/remove clears the history.
    getStateSnapshot(): HedgeMatrixStateSnapshot {
        const hedgeFields: HedgeMatrixStateSnapshot['hedgeFields'] = {};
        this.getHedges().forEach(h => {
            hedgeFields[h.id] = {
                underModification: h.underModification,
                initialExpiration: h.initialExpiration,
                template: h.template,
                anchorExpiration: h.anchorExpiration,
                modifications: h['modifications']
            };
        });
        return {
            cellDataIndex: jsonCloneObject(this._cellDataIndex),
            hedgeFields
        };
    }

    // Applies one transaction delta: undo writes the before images, redo the after images.
    // Cell-scoped — only the gesture's own cells are touched; everything else in the index
    // (ORION overlay included) stays byte-identical. Images are cloned on write so the undo
    // stack's entries stay pristine while the live index mutates on.
    applyMatrixDelta(delta: MatrixTransactionDelta, direction: 'undo' | 'redo'): void {
        delta.cells.forEach(c => {
            if (isVoid(this.getHedge(c.hedgeId))) {
                return;   // hedge gone since capture (e.g. un-Preview removed it) — never write orphan cells
            }
            const image = direction === 'undo' ? c.before : c.after;

            if (isVoid(image)) {
                const expirationContainer = this._cellDataIndex[c.strike]?.[c.hedgeId];
                if (!isVoid(expirationContainer)) {
                    delete expirationContainer[c.expiration];
                }
                return;
            }

            let hedgeContainer = this._cellDataIndex[c.strike];
            if (isVoid(hedgeContainer)) {
                hedgeContainer = {};
                this._cellDataIndex[c.strike] = hedgeContainer;
            }

            let expirationContainer = hedgeContainer[c.hedgeId];
            if (isVoid(expirationContainer)) {
                expirationContainer = {};
                hedgeContainer[c.hedgeId] = expirationContainer;
            }

            expirationContainer[c.expiration] = jsonCloneObject(image);
        });

        delta.hedgeFields.forEach(hf => {
            const hedge = this.getHedge(hf.hedgeId);
            if (isVoid(hedge)) {
                return;   // hedge removed since capture — boundaries clear history, defensive only
            }
            const image = direction === 'undo' ? hf.before : hf.after;
            if (isVoid(image)) {
                return;
            }
            hedge.underModification = image.underModification;
            hedge.initialExpiration = image.initialExpiration;
            hedge.template = image.template;
            hedge.anchorExpiration = image.anchorExpiration;
            hedge['modifications'] = image.modifications;
        });

        if (!isVoid(this._strikes)) {
            this.calculatePnls(this._strikes);
        }
    }

    setTransQty(hedge: HedgeData, strike: number, expiration: string, transQty: number) {
        this.setQty(hedge, strike, expiration, 'transQty', transQty);
    }

    setOutcomeQty(hedge: HedgeData, strike: number, expiration: string, outcomeQty: number) {
        this.setQty(hedge, strike, expiration, 'outcomeQty', outcomeQty);
    }

    onHedgeModificationFinished(hedgeData: HedgeData): boolean {
        let needToRebuildRows = false;

        Object.keys(this._cellDataIndex)
            .forEach((strike: string) => {

                const iStrike = parseInt(strike, 10);

                const hedgeContainer = this._cellDataIndex[iStrike];

                if (isVoid(hedgeContainer)) {
                    return;
                }

                const expirationContainer = hedgeContainer[hedgeData.id];

                if (isVoid(expirationContainer)) {
                    return;
                }

                const cellData = Object.values(expirationContainer);

                if (isVoid(cellData)) {
                    return;
                }

                cellData.forEach((cd, ix) => {
                    cd.transQty = cd.outcomeQty = null;
                    if (!isValidNumber(cd.qty, true)) {
                        this.removeHedgeCellData(cd);
                        needToRebuildRows = true;
                    }
                });
            });

        hedgeData.underModification = false;

        return needToRebuildRows;
    }

    getTransCost(hedgeId: string, expiration?: string): number {
        const cost = this.getHedgeCostByField(hedgeId, 'transQty', expiration);
        return cost;
    }

    getTransCostAsOwned(hedgeId: string, expiration?: string): number {
        let cost = this.getTransCost(hedgeId, expiration);
        cost *= -1;
        return cost;
    }

    getOutcomeCost(hedgeId: string, expiration?: string): number {
        const cost = this.getHedgeCostByField(hedgeId, 'outcomeQty', expiration);
        return cost;
    }

    getOriginalCost(hedgeId: string, expiration?: string): number {
        const cost = this.getHedgeCostByField(hedgeId, 'qty', expiration);
        return cost;
    }

    private getHedgeCostByField(hedgeId: string, field: keyof HedgeMatrixCellData, expiration?: string) {
        const hedgeContainers = Object.values(this._cellDataIndex);

        const expirationContainers = hedgeContainers
            .map(x => x[hedgeId])
            .filter(x => !isVoid(x));

        const cellDatas = expirationContainers.flatMap(x => Object.values(x));

        const qtyDatas = cellDatas.filter(x => !!x)
            .filter(x => isValidNumber(x[field] as number, true))

        const qtties = qtyDatas
            .filter(x => isValidNumber(x[field] as number, true))
            .map(x => x[field] as number);

        if (qtties.length === 0) {
            return null;
        }

        const hcf = findHCF(qtties);
        let ratio = hcf / this._portfolioDefaultQty;
        if (!isValidNumber(ratio, true)) {
            ratio = 1;
        }

        const hedge = this.getHedge(hedgeId);
        const hedgeUnderlying = hedge?.underlying || this._underlying;
        let costNormFactor = 1;
        if (hedgeUnderlying !== this._underlying) {
            // ES=$50/point, SPX/SPY=$100/point
            costNormFactor = isFuturesTicker(hedgeUnderlying) ? 0.5 : 1;
        }

        let prices = qtyDatas
            .filter(x => isVoid(expiration) ? true : x.expiration === expiration)
            .map(x => {
                const qty = x[field] as number;
                const quote = this._lastQuoteCache.getLastQuote(x.ticker);

                let cost = !isValidNumber(quote?.mid, true)
                    ? null
                    : quote.mid * qty;

                return !isValidNumber(cost) ? null : cost * -1;

            });
        let totalCost = prices.some(x => !isValidNumber(x))
            ? null
            : Enumerable.from(prices).sum();

        if (isValidNumber(totalCost)) {
            totalCost = totalCost / hcf;
            totalCost = totalCost * ratio;
            totalCost = totalCost * costNormFactor;

            if (field !== 'transQty') {
                totalCost *= -1;
            }
        }

        return totalCost;
    }

    onHedgeModificationStarted(hedgeData: HedgeData): boolean {
        Object.keys(this._cellDataIndex)
            .forEach(strike => {

                const iStrike = parseInt(strike);

                const hedgeContainer = this._cellDataIndex[iStrike];

                if (isVoid(hedgeContainer)) {
                    return;
                }

                const expirationContainer = hedgeContainer[hedgeData.id];

                if (isVoid(expirationContainer)) {
                    return;
                }


                const cellData = Object.values(expirationContainer);

                if (isVoid(cellData)) {
                    return;
                }

                cellData.forEach(cd => {
                    cd.outcomeQty = cd.qty;
                });
            });

        hedgeData.underModification = true;

        return false;
    }

    async addNewHedge(side: 'Call' | 'Put', underlying: string): Promise<HedgeData> {

        const hedgeData: HedgeData = {
            id: getShortUUID(),
            isNew: true,
            type: side,
            name: `* New Hedge ${this._newHedges.length + 1} *`,
            color: undefined,
            legs: [],
            underlying: underlying || this._underlying,
        };

        this._newHedges.push(hedgeData);

        return hedgeData;
    }

    async onNewHedgeExpirationChanged(hedgeId: string, expiration: string) {

        const hedgeData = this.getHedge(hedgeId);

        if (isVoid(hedgeData.template)) {
            this.updateExpirationsNonTemplatedHedge(hedgeData, expiration);
        } else {
            await this.updateTemplatedHedgeWithNewAnchorDate(hedgeData, expiration);
        }

        this.calculatePnls(this._strikes);
    }

    private updateExpirationsNonTemplatedHedge(hedgeData: HedgeData, expiration: string) {
        Object.keys(this._cellDataIndex).forEach(strike => {

            const strikeContainer: Record<string, Record<string, HedgeMatrixCellData>> = this._cellDataIndex[strike];

            if (isVoid(strikeContainer)) {
                return;
            }

            const hedgeContainer = strikeContainer[hedgeData.id];

            if (isVoid(hedgeContainer)) {
                return;
            }

            const cellDatas = Object.values(hedgeContainer) as HedgeMatrixCellData[];

            if (isVoid(cellDatas)) {
                return;
            }

            const totalQty = cellDatas
                .map(x => x.transQty)
                .reduce((a, b) => a + b);

            cellDatas.forEach(cd => delete hedgeContainer[cd.expiration]);

            this.setTransQty(hedgeData, parseInt(strike), expiration, totalQty);
        });
    }

    removeHedge(hedgeId: string) {
        const hedge = this._newHedges.find(x => x.id === hedgeId);
        const ix = this._newHedges.findIndex(x => x.id === hedgeId);
        if (ix === -1) {
            return;
        }
        this._newHedges.splice(ix, 1);

        Object.keys(this._cellDataIndex)
            .forEach(strike => {
                const strikeContainer = this._cellDataIndex[strike];
                if (isVoid(strikeContainer)) {
                    return;
                }
                delete strikeContainer[hedgeId];
            });

        // Clean up underlying context if no hedges remain for that underlying
        if (hedge) {
            const ul = hedge.underlying || this._underlying;
            if (ul !== this._underlying && this.getHedgesByUnderlying(ul).length === 0) {
                this._underlyingContexts.delete(ul);
            }
        }
    }

    getTransactionLegs(hedgeId: string): HedgeMatrixTransLeg[] {

        const cellData = this.getCellDataForHedge(hedgeId);

        const legs = cellData
            .filter(x => isValidNumber(x.transQty, true))
            .map(x => {

                const ticker = x.ticker;
                const transQty = x.transQty;

                return {
                    hedgeId,
                    ticker,
                    qty: transQty,
                    priceAsOwned: false
                }
            });

        return legs;
    }

    /**
     * Return outcome legs only for hedges that are under modification.
     * To get outcome legs disregarding hedge's status (new, modified, original) use @getNetOutcomeLegs
     * @param hedgeId
     */
    getOutcomeLegs(hedgeId: string): HedgeMatrixTransLeg[] {

        const cellData = this.getCellDataForHedge(hedgeId);

        const legs = cellData
            .filter(x => isValidNumber(x.outcomeQty, true))
            .map(x => {

                const ticker = x.ticker;
                const outcomeQty = x.outcomeQty;

                return {
                    hedgeId,
                    ticker,
                    qty: outcomeQty,
                    priceAsOwned: true
                }
            });

        return legs;
    }

    /**
     * Returns net outcome legs of hedges in its current status (includes transactions and original legs)
     * @param hedgeId
     */
    getNetOutcomeLegs(hedgeId: string): HedgeMatrixTransLeg[] {

        const hedgeData = this.getHedge(hedgeId);

        if (isVoid(hedgeData)) {
            return [];
        }

        let legs: HedgeMatrixTransLeg[] = [];

        if (hedgeData.underModification || hedgeData.isNew) {
           legs = this.getOutcomeLegs(hedgeData.id);
        } else {
            legs = this.getOriginalLegs(hedgeData.id);
        }

        return legs;
    }

    getOriginalLegs(hedgeId: string): HedgeMatrixTransLeg[] {

        const cellData = this.getCellDataForHedge(hedgeId);

        const legs = cellData
            .filter(x => isValidNumber(x.qty, true))
            .map(x => {

                const ticker = x.ticker;
                const originalQty = x.qty;

                return {
                    hedgeId,
                    ticker,
                    qty: originalQty,
                    priceAsOwned: true
                }
            });

        return legs;
    }

    calculatePnls(strikes: number[]) {
        this._strikes = strikes;
        this._callExpirationPnl = {};
        this._putExpirationPnl = {};
        this.calculateExpirationPnls('Call', strikes, this._callExpirationPnl);
        this.calculateExpirationPnls('Put', strikes, this._putExpirationPnl);
    }

    getExpirationPnl(expiration: string, strike: number, side: 'Call' | 'Put', visible: string[]) {

        const index = side === 'Call'
            ? this._callExpirationPnl
            : this._putExpirationPnl;


        const expContainer = index[expiration];

        if (isVoid(expContainer)) {
            return null;
        }

        const strikeContainer = expContainer[strike];

        if (isVoid(strikeContainer)) {
            return null;
        }

        const numbers = Object.keys(strikeContainer).filter(x => visible.indexOf(x) !== -1)
            .map(x => strikeContainer[x]);

        const total = numbers.reduce((p, c) => p + c, 0);

        return total;
    }

    private calculateExpirationPnls(side: 'Call' | 'Put', strikes: number[], index: Record<string, Record<number, Record<string, number>>>) {
        const hedges = this.getHedges();
        const sideHedges = hedges.filter(x => x.type === side);
        sideHedges.forEach(hedge => {

            const hedgeExpirations = this.getHedgeExpirations(hedge.id);
            const hedgeUnderlying = hedge.underlying || this._underlying;
            const hedgeFairValue = this.getFairValueOffset(hedgeUnderlying);
            const isNonBase = hedgeUnderlying !== this._underlying;

            // Normalization: for non-base underlyings, compute the effective multiplier ratio
            // ES=$50 vs SPX=$100 → ratio = 0.5. For base underlying, ratio = 1.
            let pnlMultiplier = 100; // Default: SPX $100/point
            if (isNonBase) {
                const ctx = this._underlyingContexts.get(hedgeUnderlying);
                if (ctx?.tradingInstrument) {
                    // Futures: pointValue directly (e.g., 50 for ES)
                    // Then normalize to SPX-equivalent by using that value directly
                    // instead of the standard 100
                    pnlMultiplier = ctx.tradingInstrument.pointValue || 50;
                }
            }

            hedgeExpirations.forEach(hedgeExpiration => {

                strikes.forEach((strike) => {

                    const hedgeId = hedge.id;

                    const hedgeIsNew = hedge.isNew;

                    let hedgeLegs = hedge.legs.slice();

                    if (hedgeIsNew) {
                        const transactionLegs = this
                            .getTransactionLegs(hedgeId)
                            .map(x => {
                                const optionTicker = parseOptionTicker(x.ticker);

                                const hp: HedgePosition = {
                                    qty: x.qty,
                                    ticker: x.ticker,
                                    groupId: x.hedgeId,
                                    type: optionTicker?.type,
                                    strike: optionTicker?.strike,
                                    expiration: optionTicker?.expiration,
                                };

                                return hp;
                            });

                        hedgeLegs = transactionLegs;

                        hedge.legs = hedgeLegs;
                    } else {

                        const expirationCellData = this.getCellDataForHedge(hedgeId)
                            .filter(x => x.expiration === hedgeExpiration);

                        const legsAtExpiration = expirationCellData.map(x => {
                            const qty = (x.qty || 0) + (x.transQty || 0);

                            const optionTicker = parseOptionTicker(x.ticker);

                            const hp: HedgePosition = {
                                qty: qty,
                                ticker: x.ticker,
                                groupId: x.hedgeId,
                                type: optionTicker?.type,
                                strike: optionTicker?.strike,
                                expiration: optionTicker?.expiration,
                            };

                            return hp;
                        });

                        hedgeLegs = legsAtExpiration;
                    }

                    // For non-base underlyings, the row's actual strike differs from the SPX-equivalent
                    // strike by the fair value offset. leg.strike comes from parseOptionTicker() and is
                    // the actual strike. So we need to use (strike + fairValue) for the row's actual strike.
                    const rowActualStrike = strike + hedgeFairValue;

                    const legPnls = hedgeLegs
                        .filter(x => x.expiration === hedgeExpiration)
                        .map(leg => {

                            let outcomeQty = leg.qty;

                            let delta = (rowActualStrike - leg.strike) * outcomeQty;

                            if (leg.type === 'Put') {
                                delta *= -1;
                            }

                            if (delta < 0) {
                                if (outcomeQty > 0) {
                                    delta = 0;
                                }
                            } else if (delta > 0) {
                                if (outcomeQty < 0) {
                                    delta = 0;
                                }
                            }

                            return delta * pnlMultiplier;

                        })
                        .reduce((p, c) => p + c, 0);

                    let expContainer = index[hedgeExpiration];

                    if (isVoid(expContainer)) {
                        expContainer = {};
                        index[hedgeExpiration] = expContainer;
                    }

                    let strikeContainer = expContainer[strike];

                    if (isVoid(strikeContainer)) {
                        strikeContainer = {};
                        expContainer[strike] = strikeContainer;
                    }

                    strikeContainer[hedgeId] = legPnls;
                });
            });
        });
    }

    getQty(strike: number, hedgeId: string): HedgeMatrixCellData[] {
        const hedgeContainer = this._cellDataIndex[strike];

        if (isVoid(hedgeContainer)) {
            return null;
        }

        const expirationContainer = hedgeContainer[hedgeId];

        if (!expirationContainer) {
            return null;
        }

        const cellData = Object.values(expirationContainer);

        return cellData || [];
    }

    setQty(hedge: HedgeData, strike: number, expiration: string, field: keyof HedgeMatrixCellData, qty: number) {
        try {
            this.setQtyInternal(hedge, strike, expiration, field, qty);
        } finally {
            if (!isVoid(this._strikes)) {
                this.calculatePnls(this._strikes);
            }
        }
    }

    private setQtyInternal(hedge: HedgeData, strike: number, expiration: string, field: keyof HedgeMatrixCellData, qty: number) {
        let hedgeContainer = this._cellDataIndex[strike];

        if (isVoid(hedgeContainer)) {
            hedgeContainer = {};
            this._cellDataIndex[strike] = hedgeContainer;
        }

        let expirationContainer = hedgeContainer[hedge.id];

        if (!expirationContainer) {
            expirationContainer = {};
            hedgeContainer[hedge.id] = expirationContainer;
        }

        let cellData = expirationContainer[expiration];

        if (isVoid(cellData)) {

            const hedgeUnderlying = hedge.underlying || this._underlying;
            const isNonBase = hedgeUnderlying !== this._underlying;
            const fairValue = isNonBase
                ? (this._underlyingContexts.get(hedgeUnderlying)?.fairValueOffset || 0)
                : 0;
            const actualStrike = strike + fairValue;

            cellData = {
                hedgeId: hedge.id,
                optionType: hedge.type,
                label: hedge.name,
                color: hedge.color,
                expiration: expiration,
                strike: strike,
                actualStrike: actualStrike,
                underlying: hedgeUnderlying,
                ticker: null
            };

            const tickerForCell = this
                .getTickerForCell(actualStrike, expiration, hedge.type, hedgeUnderlying);

            cellData.ticker = tickerForCell;

            // @ts-ignore
            cellData[field] = qty;

            expirationContainer[expiration] = cellData;
        } else {
            // @ts-ignore
            cellData[field] = qty;
        }

        const legQty = cellData.qty || 0;

        if (!isValidNumber(qty)) {

            if (field === 'transQty') {
                cellData.transQty = null;
                cellData.outcomeQty = legQty || null;
            } else if (field === 'outcomeQty') {
                if (legQty !== 0) { // existing leg
                    cellData.transQty = -legQty;
                    cellData.outcomeQty = null;
                } else { // deleting just created leg
                    cellData.transQty = null;
                    cellData.outcomeQty = legQty || null;
                }
            }
        } else {
            if (field === 'outcomeQty') {
                const outcomeQty = cellData.outcomeQty || 0;
                const trans = (outcomeQty - legQty) || null;
                cellData.transQty = trans;
            } else {
                const transQty = cellData.transQty || 0;
                const outcome = (legQty + transQty) || null;
                cellData.outcomeQty = outcome;
            }
        }

        if (!isValidNumber(cellData.qty, true) &&
            !isValidNumber(cellData.transQty, true) &&
            !isValidNumber(cellData.outcomeQty, true)) {
            this.removeHedgeCellData(cellData);
        }

        if (hedge.template) {
            hedge.template = undefined;
        }
    }

    async getPortfolioPositions(portfolio: ApgPortfolio): Promise<BeforePositionDto[][]> {
        const positions = await this._apgDataService.getPortfolioPositions(portfolio);
        return positions;
    }

    getCellDataForHedge(hedgeId: string): HedgeMatrixCellData[] {
        const hedgeContainers = Object.values(this._cellDataIndex);

        const expirationContainers = hedgeContainers
            .map(x => x[hedgeId])
            .filter(x => !isVoid(x));

        const cellDatas = expirationContainers.flatMap(x => Object.values(x));

        return cellDatas;
    }

    doesHaveMultipleExpirations(hedgeId: string): boolean {
        const hedgeExpirations = this.getHedgeExpirations(hedgeId);
        return hedgeExpirations.length > 1;
    }

    getHedgeExpirations(hedgeId: string): string[] {
        const cellData = this.getCellDataForHedge(hedgeId);

        if (isVoid(cellData)) {
            const defaultExpiration = this.getHedgeDefaultExpiration(hedgeId);
            return [defaultExpiration];
        }

        const expirations = Enumerable.from(cellData)
            .select(x => x.expiration)
            .where(x => !isVoid(x))
            .distinct()
            .toArray();

        return expirations;
    }

    getHedgeExpirationsByColumn(hedgeId: string, state: 'original' | 'trans' | 'outcome'): string[] {
        let cellData = this.getCellDataForHedge(hedgeId);

        switch (state) {
            case "original":
                cellData = cellData.filter(x => isValidNumber(x.qty, true));
                break;
            case "trans":
                cellData = cellData.filter(x => isValidNumber(x.transQty, true));
                break;
            case "outcome":
                cellData = cellData.filter(x => isValidNumber(x.outcomeQty, true));
                break;
        }

        if (isVoid(cellData)) {
            const defaultExpiration = this.getHedgeDefaultExpiration(hedgeId);
            return [defaultExpiration];
        }

        const expirations = Enumerable.from(cellData)
            .select(x => x.expiration)
            .where(x => !isVoid(x))
            .distinct()
            .toArray();

        return expirations;
    }

    hedgeContainsExpiration(hedgeId: string, expiration: string): boolean {
        const expirations = this.getHedgeExpirations(hedgeId);
        if (expirations.length > 0) {
            return expirations.includes(expiration);
        }
        const defaultExpiration = this.getHedgeDefaultExpiration(hedgeId);
        return defaultExpiration === expiration;
    }

    getHedgeDefaultExpiration(hedgeId: string): string {

        const hedgeData = this.getHedge(hedgeId);

        const cellData = this.getCellDataForHedge(hedgeId);

        if (isVoid(cellData)) {

            if (hedgeData) {
                if (hedgeData.initialExpiration) {
                    return hedgeData.initialExpiration;
                }
            }

            // For non-base underlyings (ES contracts), use their own chain's nearest expiration
            const hedgeUnderlying = hedgeData?.underlying;
            const isNonBase = hedgeUnderlying && hedgeUnderlying !== this._underlying;

            if (isNonBase) {
                const nearest = this.getNearestExpiration(hedgeUnderlying);
                return nearest?.optionExpirationDate;
            }

            // Base underlying: scan base hedges for expirations
            const allAvailableExpirations = this.getHedges()
                .filter(x => !x.underlying || x.underlying === this._underlying)
                .flatMap(x => {
                    const strings = x.legs.map(y => y.expiration)
                        .concat(x.initialExpiration)
                        .filter(z => !!z)
                        .filter((v, ix, arr) => arr.indexOf(v) === ix);
                    return strings;
                })
                .sort();

            const mostRecentExpiration = allAvailableExpirations
                .filter(x => !this._optionChainService.isExpirationExpired(x))[0];

            return mostRecentExpiration ? mostRecentExpiration : this.getNearestExpiration()?.optionExpirationDate;
        }

        const sortedExpirations = cellData.map(x => x.expiration)
            .sort();

        const defaultExpiration = sortedExpirations[0];

        return defaultExpiration;
    }

    setCellData(copy: HedgeMatrixCellData[]) {
        copy.forEach((d) => {
            this._cellDataIndex[d.strike][d.hedgeId][d.expiration] = d;
        });
        this.calculatePnls(this._strikes);
    }

    private async updateTemplatedHedgeWithNewAnchorDate(hedgeData: HedgeData, anchorDate: string) {

        const cellData = this.getCellDataForHedge(hedgeData.id);

        if (!isVoid(cellData)) {
            cellData.forEach(cd => this.removeHedgeCellData(cd));
        } else {
            console.error('templated hedge must have legs before changing anchor date');
            return;
        }

        const side = cellData[0].optionType;

        let baseStrike: number;

        if (side === 'Call') {
            baseStrike = Enumerable.from(cellData).orderBy(x => x.strike).first().strike;
        } else if (side === 'Put') {
            baseStrike = Enumerable.from(cellData).orderByDescending(x => x.strike).first().strike;
        } else {
            console.error('Unknown option type');
        }

        let legs = hedgeData.template.strategyLegs.slice();

        if (side === 'Call') {
            legs.reverse();
        }

        const chain = await this._optionChainService.getChain(this._underlying);

        const hasNegativeOffsets = legs
            .filter(x => x.type === 'leg')
            .some(l => l.expirationOffset < 0);

        let anchorExpirationDescriptor = chain.expirations
            .find(x => x.optionExpirationDate === anchorDate);

        if (hasNegativeOffsets) {
            const offsets = legs
                .filter(x => x.type === 'leg')
                .map(x => x.expirationOffset || 0);

            const maxNegativeOffset = Math.min(...offsets);

            const daysBackAvailable = maxNegativeOffset + anchorExpirationDescriptor.daysToExpiration;

            if (daysBackAvailable < 0) {
                throw new Error('Negative expiration offsets for some of the legs, reference non-existing expirations');
            }

        }

        hedgeData.anchorExpiration = anchorExpirationDescriptor;

        const strikeMultiplier = side === 'Call' ? 1 : -1;

        for (let leg of legs) {
            if (leg.type === 'width') {
                baseStrike += (leg.width * strikeMultiplier);
            } else {
                let expiration = anchorExpirationDescriptor.optionExpirationDate;

                const qtyMultiplier = leg.side === 'Buy' ? 1 : -1;

                if (isValidNumber(leg.expirationOffset)) {
                    const offsetExpiration = findOffsetExpiration(
                        anchorExpirationDescriptor,
                        leg.expirationOffset,
                        chain
                    );

                    if (isVoid(offsetExpiration)) {
                        break;
                    }

                    expiration = offsetExpiration.optionExpirationDate;
                }

                this.setTransQty(
                    hedgeData,
                    baseStrike,
                    expiration,
                    leg.qty * this._portfolioDefaultQty * qtyMultiplier
                );
            }
        }
    }

    /**
     * STEPPING ON A STRIKE — the pending trades at this contract would take the book's net
     * position through zero and out the other side.
     *
     *     E = net signed qty at the contract, ALL hedges aggregated   (Σ qty)
     *     N = net signed qty of the pending trades at the contract    (Σ transQty)
     *
     *     STEP  <=>  E != 0  AND  (E+N) != 0  AND  sign(E+N) != sign(E)
     *
     * This is `SteppingRule.FindReversals` (backend, CR-20260821-002) asked of the matrix. It is a
     * BROKER-EXECUTABILITY rule — US brokers dislike an order that reverses a held position — so
     * increases, reductions and flat-outs are all legal, however large.
     *
     * ⚠⚠ THIS REPLACED A DIFFERENT QUESTION, AND THE OLD ONE IGNORED SIGN. Until 2026-08-24 this
     * asked *"do two hedges hold this contract, and is one of them trading?"*, which painted every
     * reduction and every flat-out as a violation and permitted a reversal inside a single hedge.
     * Both engines dropped that question on 2026-08-21; the matrix kept it three days longer.
     *
     * ⚠ CONTAINER COUNT IS NOT PART OF THE TEST. Owner, 2026-08-24: *"self-reversal inside one
     * hedge also counts... no matter where the reverse happens, it should be highlighted."* One
     * hedge crossing its own zero is the same order to the broker as three hedges doing it between
     * them, so anything reinstating a two-hedge precondition is enforcing a repealed rule.
     *
     * ⚠ `underlying` IS PART OF THE CONTRACT KEY, AND IT MUST BE. Rows are indexed by
     * SPX-EQUIVALENT strike (`indexStrike = actualStrike - fairValueOffset`), so an ES leg and an
     * SPX leg can share one row while being two different instruments at the broker. Owner:
     * *"ES and SPX are different contracts, so they're not stepping on each other."* Drop this
     * filter and the row index alone invents reversals nobody can trade.
     *
     * `Σ transQty` is the whole of `N` whichever column the user typed into: `setQtyInternal`
     * keeps the pair in step both ways (an `outcomeQty` edit sets `transQty = outcomeQty - qty`).
     * A NEW hedge carries its quantity in `transQty` with no `qty`, so it moves `N` and not `E`.
     */
    stepsOnStrike(strike: number, expiration: string, side: 'Call' | 'Put', underlying: string): boolean {
        const hedgesContainer = this._cellDataIndex[strike];

        if (isVoid(hedgesContainer)) {
            return false;
        }

        const contractCells = Object.values(hedgesContainer)
            .flatMap(x => Object.values(x))
            .filter(x => x.optionType === side)
            .filter(x => x.expiration === expiration)
            .filter(x => x.underlying === underlying);

        let existingNet = 0;

        let pendingNet = 0;

        contractCells.forEach(x => {
            existingNet += x.qty || 0;
            pendingNet += x.transQty || 0;
        });

        if (existingNet === 0) {
            return false; // nothing held here — nothing to reverse
        }

        const after = existingNet + pendingNet;

        // Reaching zero is FLAT, not reversed. Crossing it is the step.
        return after !== 0 && Math.sign(after) !== Math.sign(existingNet);
    }

    getTotalOriginalCost(side?: 'Call' | 'Put', visibleHedges?: string[]): number {

        let hedges = this.getHedges().filter(h => isVoid(side) ? true : h.type === side);

        hedges = hedges.filter(h => visibleHedges === undefined ? true : visibleHedges.includes(h.id));

        const numbers = hedges
            .map(x => this.getOriginalCost(x.id));

        if (numbers.every(x => !isValidNumber(x, true))) {
            return null;
        }

        const total = numbers.filter(x => isValidNumber(x)).reduce((p, c) => p + c);

        return total;
    }

    getTotalTransCost(side?: 'Call' | 'Put', visibleHedges?: string[]): number {

        let hedges = this.getHedges().filter(h => isVoid(side) ? true : h.type === side);

        hedges = hedges.filter(h => visibleHedges === undefined ? true : visibleHedges.includes(h.id));

        const numbers = hedges
            .map(x => this.getTransCost(x.id));

        if (numbers.every(x => !isValidNumber(x, true))) {
            return null;
        }

        const total = numbers.filter(x => isValidNumber(x)).reduce((p, c) => p + c);

        return total;
    }

    rollLeg(hedge: HedgeData, strike: number, expiration: string, qty: number) {
        const cellData = this.getCellData(strike, hedge.id);
        cellData.forEach(x => this.setQty(hedge, x.strike, x.expiration, 'outcomeQty', null));
        this.setQty(hedge, strike, expiration, 'outcomeQty', qty);
    }

    hasAnyTransactions(side?: string) {
        const hasAnyTransactions = this.getHedges()
            .filter(h => isVoid(side) ? true : h.type === side)
            .some( hedge =>
                this.getCellDataForHedge(hedge.id)
                    .some(cellData => isValidNumber(cellData.transQty, true)));
        return hasAnyTransactions;
    }
}


export function findOffsetExpiration(baseExpiration: OptionExpirationDescriptor, offset: number, chain: GetOptionChainShellResponse): OptionExpirationDescriptor {

    if (!isValidNumber(offset)) {
        offset = 0;
    }

    if (offset === 0) {
        return baseExpiration;
    }

    const expirations = chain.expirations.slice();

    const currentDt = DateTime.fromISO(baseExpiration.optionExpirationDate);

    const calculatedDate = currentDt.plusBusiness({days: offset});

    const daysDiff = calculatedDate.diff(currentDt, 'days').toObject().days;

    const desiredDte = daysDiff + baseExpiration.daysToExpiration;

    let targetExpirationDescriptor: OptionExpirationDescriptor;

    if (offset > 0) {

        targetExpirationDescriptor = expirations.find(x => x.daysToExpiration >= desiredDte);

    } else if (offset < 0) {

        targetExpirationDescriptor = expirations
            .reverse()
            .find(x => x.daysToExpiration <= desiredDte);

    }

    return targetExpirationDescriptor;
}
